"use client";

import { ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const manageLinks = [
  { id: "treatments", label: "Treatments", path: "/aesthetics/manage/treatments" },
  { id: "practitioner", label: "Practitioner", path: "/aesthetics/manage/practitioner" },
  // { id: "categories", label: "Categories", path: "/manage/categories" },
  // { id: "employee", label: "Employee", path: "/manage/employee" },
  // { id: "memberships", label: "Memberships", path: "/manage/memberships" },
  // { id: "stocktakes", label: "Stocktakes", path: "/manage/stocktakes" },
  // { id: "stockorders", label: "Stock Orders", path: "/manage/stockorders" },
  // { id: "suppliers", label: "Suppliers", path: "/manage/suppliers" },
];

export default function AestheticManageSubSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState("services");
  const [isEmployeeMenuOpen, setIsEmployeeMenuOpen] = useState(false);

  // Sync activeId with current pathname
  useEffect(() => {
    const current = manageLinks.find((link) =>
      location.pathname.startsWith(link.path)
    );
    if (current) {
      setActiveId(current.id);
      // If the "Employee" section is active, keep the submenu open
      if (current.id === "employee" || location.pathname.startsWith("/manage/employee")) {
        setIsEmployeeMenuOpen(true);
      } else {
        setIsEmployeeMenuOpen(false); // Ensure submenu closes for other sections
      }
    }
  }, [location.pathname]);

  const handleMainLinkClick = (item: { id: string, path: string }) => {
    // Navigate to the main section link and ensure submenu for Employee is closed for other links
    setActiveId(item.id);
    navigate(item.path);
    if (item.id !== "employee") {
      setIsEmployeeMenuOpen(false); // Close the Employee submenu for non-employee links
    }
  };

  const handleSubmenuItemClick = (submenuPath: string) => {
    // Keep the "Employee" section active and navigate to the submenu item
    setActiveId("employee");
    navigate(submenuPath);
  };

  return (
    <div className="relative w-full lg:w-56 lg:ml-2 lg:mt-3">
      <div className="flex flex-row lg:flex-col overflow-x-auto lg:overflow-y-auto w-full bg-white py-2 lg:py-4 border-b lg:border-none scrollbar-hide relative">
        {manageLinks.map((item) => {
          const isActive = activeId === item.id;
          const isEmployee = item.id === "employee";

          return (
            <div key={item.id} className="flex flex-row lg:flex-col items-center lg:items-stretch">
              <button
                onClick={() => handleMainLinkClick(item)}
                className={cn(
                  "relative flex items-center px-4 py-2 text-[14px] lg:text-[15px] font-medium text-left transition-all duration-200 group whitespace-nowrap",
                  isActive
                    ? "text-purple-600"
                    : "text-gray-800 hover:text-purple-600"
                )}
              >
                <ChevronRight
                  className={cn(
                    "absolute left-0 w-4 h-4 opacity-0 transform transition-all duration-200 hidden lg:block",
                    isActive
                      ? "opacity-100 text-purple-600 translate-x-0"
                      : "group-hover:opacity-100 group-hover:text-purple-600 group-hover:translate-x-0"
                  )}
                />
                <span
                  className={cn(
                    "transition-all duration-200",
                    isActive ? "lg:translate-x-4" : "lg:group-hover:translate-x-4",
                    isActive && "border-b-2 border-purple-600 lg:border-none pb-1 lg:pb-0"
                  )}
                >
                  {item.label}
                </span>
              </button>

              {/* Submenu for Employee */}
              {isEmployee && isEmployeeMenuOpen && (
                <div className="flex flex-row lg:flex-col items-center lg:items-stretch lg:pl-8 lg:mt-2">
                  {[
                    { label: "Scheduled Shifts", path: "/manage/employee/scheduled-shifts" },
                    { label: "Timesheets", path: "/manage/employee/timesheets" },
                  ].map((submenuItem) => {
                    const isSubmenuActive = location.pathname === submenuItem.path;
                    return (
                      <button
                        key={submenuItem.path}
                        onClick={() => handleSubmenuItemClick(submenuItem.path)}
                        className={cn(
                          "block px-3 py-1 lg:px-4 lg:py-2 text-[12px] lg:text-sm font-medium text-left rounded-lg transition-all w-full whitespace-nowrap",
                          isSubmenuActive
                            ? "text-purple-600 bg-purple-50 font-semibold"
                            : "text-gray-700 hover:text-purple-600 hover:bg-gray-100"
                        )}
                      >
                        {submenuItem.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        {/* Right side fade for mobile scroll */}
        <div className="lg:hidden absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none" />
      </div>
    </div>
  );
}
